/**
 * @file ProfessionalRoute.jsx
 *
 * ProfessionalRoute
 *
 * Wraps routes that require:
 * 1. Authentication
 * 2. PROFESIONAL role
 * 3. Completed onboarding (onboardingComplete = true)
 *
 * If onboarding is incomplete, redirects to /onboarding.
 * Used for all professional-only pages so they can't skip the wizard.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { Spinner } from "../ui";

/**
 * Route guard that requires authentication, PROFESIONAL role, and completed onboarding.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render when access is granted
 * @returns {React.ReactElement}
 */
const ProfessionalRoute = ({ children }) => {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Only fetch the professional profile when the user is authenticated as PROFESIONAL
  const shouldFetchProfile = isAuthenticated && user?.role === "PROFESIONAL";
  const { profile, isLoading: profileLoading } = useProfessionalProfile(shouldFetchProfile);

  if (authLoading || (shouldFetchProfile && profileLoading)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60dvh" }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== "PROFESIONAL") {
    return <Navigate to="/" replace />;
  }

  // Redirect to onboarding if not yet complete
  if (profile && !profile.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

export default ProfessionalRoute;
